import { datadogRum, type RumEvent } from "@datadog/browser-rum";
import { datadogLogs, type LogsEvent } from "@datadog/browser-logs";
import envConfig from "@/config/env";

// Query params that carry auth secrets or one-time artifacts. The SSO
// callback lands on a URL like ``/sso/callback?code=<kc-code>&state=<nonce>``;
// RUM/Logs auto-capture ``view.url`` (and resource/http URLs), so without
// redaction these one-time credentials would be ingested into third-party
// telemetry — outside the auth boundary. Matched case-insensitively.
const SENSITIVE_QUERY_PARAMS = new Set([
  "code",
  "state",
  "session_state",
  "id_token",
  "access_token",
  "refresh_token",
  "token",
  "error_description",
]);

/**
 * Replaces the values of known auth-bearing query params with "REDACTED".
 * Returns the input untouched when there is nothing to redact or the value
 * is not an absolute, parseable URL.
 */
export function redactSensitiveUrl(rawUrl?: string): string | undefined {
  if (!rawUrl) return rawUrl;
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    // Relative or malformed; nothing safe to parse, leave as-is.
    return rawUrl;
  }
  let mutated = false;
  for (const key of Array.from(url.searchParams.keys())) {
    if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
      url.searchParams.set(key, "REDACTED");
      mutated = true;
    }
  }
  return mutated ? url.toString() : rawUrl;
}

function scrubRumEvent(event: RumEvent): boolean {
  if (event.view?.url) {
    event.view.url = redactSensitiveUrl(event.view.url) ?? event.view.url;
  }
  if (event.view?.referrer) {
    event.view.referrer =
      redactSensitiveUrl(event.view.referrer) ?? event.view.referrer;
  }
  if (event.type === "resource" && event.resource?.url) {
    event.resource.url =
      redactSensitiveUrl(event.resource.url) ?? event.resource.url;
  }
  return true;
}

function scrubLogsEvent(event: LogsEvent): boolean {
  if (event.view?.url) {
    event.view.url = redactSensitiveUrl(event.view.url) ?? event.view.url;
  }
  if (event.view?.referrer) {
    event.view.referrer =
      redactSensitiveUrl(event.view.referrer) ?? event.view.referrer;
  }
  if (event.http?.url) {
    event.http.url = redactSensitiveUrl(event.http.url) ?? event.http.url;
  }
  return true;
}

export function initDatadog() {
  if (!envConfig.ddApplicationId || !envConfig.ddClientToken) {
    console.warn("[Datadog] Skipping init: VITE_DD_APPLICATION_ID or VITE_DD_CLIENT_TOKEN is not set.");
    return;
  }

  datadogLogs.init({
    clientToken: envConfig.ddClientToken,
    site: "datadoghq.com",
    service: "stormimpact-client",
    env: envConfig.ddEnv,
    version: envConfig.appVersion,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error"],
    sessionSampleRate: 100,
    beforeSend: scrubLogsEvent,
  });

  datadogRum.init({
    applicationId: envConfig.ddApplicationId,
    clientToken: envConfig.ddClientToken,
    site: "datadoghq.com",
    service: "stormimpact-client",
    env: envConfig.ddEnv,
    version: envConfig.appVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    allowedTracingUrls: [envConfig.apiBaseUrl],
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: scrubRumEvent,
  });
}

export function setDatadogUser(user: {
  id: string;
  email: string;
  name: string;
  role: string;
  orgId: string;
  orgName: string;
  orgSlug: string;
}) {
  try {
    datadogRum.setUser({
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      org_id: user.orgId,
      org_name: user.orgName,
      org_slug: user.orgSlug,
    });
    datadogLogs.setUser({
      id: user.id,
      email: user.email,
      name: user.name,
    });
  } catch {
    // never let DD failures surface to the user
  }
}

export function clearDatadogUser() {
  try {
    datadogRum.clearUser();
    datadogLogs.clearUser();
  } catch {
    // never let DD failures surface to the user
  }
}

export function trackRumError(error: unknown, context?: Record<string, unknown>) {
  try {
    datadogRum.addError(error, context);
  } catch {
    // never let DD failures surface to the user
  }
}

export function trackRumAction(name: string, context?: Record<string, unknown>) {
  try {
    datadogRum.addAction(name, context);
  } catch {
    // never let DD failures surface to the user
  }
}
